import { useMemo, useRef, useState } from 'react'
import { Image as ImageIcon, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { uploadMenuImage } from '@/lib/queries/usePOS'
import { resolvePOSImageUrl } from '@/lib/pos/images'

interface Props {
  label: string
  value?: string | null
  onChange: (value: string) => void
  description?: string
  imageClassName?: string
}

export function MenuImageField({
  label,
  value,
  onChange,
  description = 'JPG, PNG, or WebP',
  imageClassName = 'h-36',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const previewUrl = useMemo(() => resolvePOSImageUrl(value), [value])

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.')
      return
    }

    setUploading(true)
    setError(null)
    try {
      const imageUrl = await uploadMenuImage(file)
      onChange(imageUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image upload failed.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</p>
          <p className="text-xs text-[var(--color-text-muted)]">{description}</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
          {previewUrl ? 'Change' : 'Upload'}
        </Button>
      </div>

      {previewUrl ? (
        <div className="relative overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]">
          <img src={previewUrl} alt={label} className={`w-full object-cover ${imageClassName}`} />
          <button
            type="button"
            aria-label={`Remove ${label.toLowerCase()}`}
            title="Remove image"
            onClick={() => onChange('')}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-black/60 text-white transition-colors hover:bg-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-28 w-full flex-col items-center justify-center gap-2 rounded-[var(--radius-md)] border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-accent-amber)]/50 hover:text-[var(--color-text-secondary)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ImageIcon className="h-5 w-5" />
          <span className="text-sm">{uploading ? 'Uploading...' : 'Click to upload image'}</span>
        </button>
      )}

      {error && <p className="text-xs text-[var(--color-accent-red)]">{error}</p>}
    </div>
  )
}
